import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";

import { useMe } from "@/hooks/use-auth";
import {
  useCompleteExpense,
  useCreateExpense,
  useUpdateExpense,
} from "@/hooks/use-expenses";
import { compressImage } from "@/lib/compress-image";
import { getErrorMessage } from "@/lib/error-message";
import { newIdempotencyKey } from "@/lib/idempotency";
import { expenseService } from "@/services/expense";
import { storageService } from "@/services/storage";

import {
  defaultExpenseValues,
  emptyExpenseItem,
  expenseSchema,
  type ExpenseFormValues,
} from "./expense-schema";
import {
  buildExpenseSubmitPayload,
  calculateExpenseGrandTotal,
  calculateItemsSubtotal,
  inferDiscountOption,
  inferTaxOption,
} from "./expense-utils";

import type { DiscountOption, TaxOption } from "./expense-utils";
import type { Expense, ExpenseCostImpactPreview } from "@artisancode/api-types";

export type { DiscountOption, TaxOption } from "./expense-utils";

export interface ExistingReceipt {
  key: string;
  url?: string;
  name?: string;
}

export function useExpenseForm(
  open: boolean,
  editing: Expense | null,
  onSuccess: () => void,
) {
  const { mutateAsync: create, isPending: isCreating } = useCreateExpense();
  const { mutateAsync: update, isPending: isUpdating } = useUpdateExpense();
  const { mutateAsync: complete, isPending: isCompleting } =
    useCompleteExpense();
  const { data: me } = useMe();

  // One key per opened form: a retry after a network error reuses it, so the
  // server returns the expense it already booked instead of creating a second.
  const idempotencyKey = useRef(newIdempotencyKey("exp"));

  const [isUploading, setIsUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingReceipts, setExistingReceipts] = useState<ExistingReceipt[]>(
    [],
  );
  const [taxOption, setTaxOption] = useState<TaxOption>("none");
  const [discountOption, setDiscountOption] = useState<DiscountOption>("none");

  const isSaving = isCreating || isUpdating || isCompleting || isUploading;

  const form = useForm<ExpenseFormValues>({
    resolver: zodResolver(expenseSchema),
    defaultValues: defaultExpenseValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "items",
  });

  const watchedItems = form.watch("items");
  const watchedDiscount = form.watch("discountAmount");
  const watchedTax = form.watch("taxAmount");

  const itemsSubtotal = calculateItemsSubtotal(watchedItems);
  const currentDiscount = Number(watchedDiscount) || 0;
  const currentTax = Number(watchedTax) || 0;
  const grandTotal = calculateExpenseGrandTotal(
    itemsSubtotal,
    currentDiscount,
    currentTax,
  );

  const selectDiscountOption = (option: DiscountOption) => {
    setDiscountOption(option);
    if (option === "none") {
      form.setValue("discountAmount", 0, { shouldValidate: true });
    } else if (option === "disc5") {
      form.setValue("discountAmount", Math.round(itemsSubtotal * 0.05), {
        shouldValidate: true,
      });
    } else if (option === "disc10") {
      form.setValue("discountAmount", Math.round(itemsSubtotal * 0.1), {
        shouldValidate: true,
      });
    }
  };

  const selectTaxOption = (option: TaxOption) => {
    setTaxOption(option);
    const taxable = Math.max(
      0,
      itemsSubtotal - (Number(form.getValues("discountAmount")) || 0),
    );
    if (option === "none") {
      form.setValue("taxAmount", 0, { shouldValidate: true });
    } else if (option === "ppn11") {
      form.setValue("taxAmount", Math.round(taxable * 0.11), {
        shouldValidate: true,
      });
    } else if (option === "pb1_10") {
      form.setValue("taxAmount", Math.round(taxable * 0.1), {
        shouldValidate: true,
      });
    }
  };

  // Recalculate discount & tax if items or options change while an active percentage is chosen
  useEffect(() => {
    if (!open) return;
    let disc = Number(form.getValues("discountAmount")) || 0;
    if (discountOption === "disc5") {
      disc = Math.round(itemsSubtotal * 0.05);
      form.setValue("discountAmount", disc);
    } else if (discountOption === "disc10") {
      disc = Math.round(itemsSubtotal * 0.1);
      form.setValue("discountAmount", disc);
    } else if (discountOption === "none") {
      disc = 0;
      form.setValue("discountAmount", 0);
    }

    const taxable = Math.max(0, itemsSubtotal - disc);
    if (taxOption === "ppn11") {
      form.setValue("taxAmount", Math.round(taxable * 0.11));
    } else if (taxOption === "pb1_10") {
      form.setValue("taxAmount", Math.round(taxable * 0.1));
    } else if (taxOption === "none") {
      form.setValue("taxAmount", 0);
    }
  }, [itemsSubtotal, discountOption, taxOption, open, form]);

  useEffect(() => {
    if (open) {
      idempotencyKey.current = newIdempotencyKey("exp");
      setSelectedFiles([]);
      if (editing) {
        const receipts: ExistingReceipt[] = [];
        const keys =
          editing.receiptKeys && editing.receiptKeys.length > 0
            ? editing.receiptKeys
            : editing.receiptKey
              ? [editing.receiptKey]
              : [];

        keys.forEach((key, idx) => {
          const url =
            editing.receiptUrls?.[idx] ||
            (idx === 0 ? (editing.receiptUrl ?? undefined) : undefined);
          const name = key.split("/").pop() || key;
          receipts.push({ key, url, name });
        });
        setExistingReceipts(receipts);

        const items =
          editing.items && editing.items.length > 0
            ? editing.items.map((i) => ({
                description: i.description,
                itemType: i.itemType ?? null,
                itemId: i.itemId ?? null,
                uomId: i.uomId ?? null,
                qty: i.qty,
                // a stock line bought in its base unit may carry no qtyInUom; qty is then the purchase qty
                qtyInUom: i.qtyInUom ?? (i.itemType ? i.qty : null),
                pricePerUnit: i.pricePerUnit,
              }))
            : [
                {
                  description: editing.description || "",
                  itemType: null,
                  itemId: null,
                  uomId: null,
                  qty: editing.qty || 1,
                  qtyInUom: null,
                  pricePerUnit: editing.pricePerUnit || 0,
                },
              ];

        const tax = editing.taxAmount ?? 0;
        const sub =
          editing.subtotalAmount ||
          (editing.items
            ? editing.items.reduce((s, it) => s + it.subtotal, 0)
            : editing.qty * editing.pricePerUnit);
        const disc = editing.discountAmount ?? 0;
        setDiscountOption(inferDiscountOption(sub, disc));

        const taxable = Math.max(0, sub - disc);
        setTaxOption(inferTaxOption(taxable, tax));

        form.reset({
          date: editing.date,
          category: editing.category,
          description: editing.description,
          items,
          qty: editing.qty,
          pricePerUnit: editing.pricePerUnit,
          discountAmount: editing.discountAmount ?? 0,
          taxAmount: editing.taxAmount ?? 0,
          paymentMethod: editing.paymentMethod,
          fundingSource: editing.fundingSource,
          referenceNote: editing.referenceNote ?? "",
          receiptKey: editing.receiptKey,
          receiptKeys: editing.receiptKeys ?? [],
        });
      } else {
        setDiscountOption("none");
        setTaxOption("none");
        setExistingReceipts([]);
        form.reset({
          ...defaultExpenseValues,
          workspaceId: me?.workspaceId ?? "",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, me?.workspaceId]);

  const addFiles = async (files: FileList | File[]) => {
    const validFiles: File[] = [];
    const MAX_SIZE = 15 * 1024 * 1024; // 15MB
    for (const f of Array.from(files)) {
      if (f.size > MAX_SIZE) {
        toast.error(`Ukuran berkas "${f.name}" melebihi batas 15MB.`);
        continue;
      }
      validFiles.push(f);
    }
    const compressed = await Promise.all(validFiles.map(compressImage));
    setSelectedFiles((prev) => [...prev, ...compressed]);
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingReceipt = (key: string) => {
    setExistingReceipts((prev) => prev.filter((r) => r.key !== key));
  };

  const addItem = (kind: "operational" | "restock" = "operational") => {
    append({
      ...emptyExpenseItem,
      itemType: kind === "restock" ? "raw_material" : null,
    });
  };

  const removeItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const [pendingImpact, setPendingImpact] =
    useState<ExpenseCostImpactPreview | null>(null);
  const [pendingSave, setPendingSave] = useState<{
    values: ExpenseFormValues;
    mode: "done";
  } | null>(null);

  /** mode "draft" keeps it editable and books nothing; "done" approves it (stock is booked). */
  async function save(
    values: ExpenseFormValues,
    mode: "draft" | "done",
    acknowledgeCostImpact = false,
  ) {
    let uploadedKeys: string[] = [];

    if (selectedFiles.length > 0) {
      setIsUploading(true);
      try {
        uploadedKeys = await storageService.uploadMultipleFiles(
          selectedFiles,
          "expenses",
        );
      } catch (err: unknown) {
        setIsUploading(false);
        const msg = getErrorMessage(err, "Gagal mengunggah berkas bukti.");
        toast.error(msg);
        return;
      }
      setIsUploading(false);
    }

    const payload = buildExpenseSubmitPayload(
      values,
      existingReceipts.map((r) => r.key),
      uploadedKeys,
    );

    // If mode is "done" and not yet acknowledged, preview cost impact first
    if (mode === "done" && !acknowledgeCostImpact) {
      const hasRawMaterials = (payload.items ?? []).some(
        (i) => i.itemType === "raw_material" && !!i.itemId,
      );
      if (hasRawMaterials) {
        try {
          const preview = await expenseService.previewCost({
            ...payload,
            status: "done",
          });
          if (preview.hasImpact) {
            setPendingImpact(preview);
            setPendingSave({ values, mode: "done" });
            return;
          }
        } catch {
          // If preview fails, fall through to submit which handles 409
        }
      }
    }

    try {
      if (editing) {
        await update({ id: editing.id, ...payload });
        if (mode === "done") {
          await complete({
            id: editing.id,
            acknowledgeCostImpact,
          });
        }
      } else {
        await create({
          body: { ...payload, status: mode, acknowledgeCostImpact },
          idempotencyKey: idempotencyKey.current,
        });
      }
      toast.success(
        mode === "done"
          ? "Pengeluaran disimpan dan diselesaikan."
          : "Draft pengeluaran disimpan.",
      );
      onSuccess();
    } catch (err: unknown) {
      const appErr = err as {
        data?: ExpenseCostImpactPreview;
        code?: string;
        message?: string;
      };
      if (appErr?.data?.hasImpact) {
        setPendingImpact(appErr.data);
        setPendingSave({ values, mode: "done" });
        return;
      }
      toast.error(getErrorMessage(err, "Gagal menyimpan pengeluaran."));
    }
  }

  const confirmImpact = async () => {
    if (!pendingSave) return;
    const { values, mode } = pendingSave;
    setPendingImpact(null);
    setPendingSave(null);
    await save(values, mode, true);
  };

  const cancelImpact = () => {
    setPendingImpact(null);
    setPendingSave(null);
  };

  return {
    form,
    isSaving,
    isUploading,
    selectedFiles,
    existingReceipts,
    addFiles,
    removeSelectedFile,
    removeExistingReceipt,
    fields,
    addItem,
    removeItem,
    itemsSubtotal,
    grandTotal,
    taxOption,
    setTaxOption,
    selectTaxOption,
    discountOption,
    setDiscountOption,
    selectDiscountOption,
    pendingImpact,
    confirmImpact,
    cancelImpact,
    submit: (mode: "draft" | "done") =>
      form.handleSubmit((values) => save(values, mode)),
  };
}
